import React, { useState, useEffect } from 'react';
import {
    BarChart,
    LineChart,
    File,
    Users,
    HardDrive,
    Calendar,
    Download,
    Filter,
    RefreshCw,
    ChevronDown,
    Clock,
    AlertCircle,
    Check
} from 'lucide-react';
import AdminApi from '../../apis/adminApis';
import UserGrowthChart from './charts/UserGrowthChart';
import MediaTypeDistributionChart from './charts/MediaTypeDistributionChart';
import StorageUsageChart from './charts/StorageUsageChart';
import ActivityTimelineChart from './charts/ActivityTimelineChart';
import StatCard from './components/StatCard';
import { downloadReport } from './utils/Report';

interface Stats {
    users: {
        total: number;
        active: number;
        inactive: number;
        new_today: number;
        new_this_week: number;
        new_this_month: number;
        by_month: Array<{
            month: string;
            count: number;
        }>;
        by_role: {
            superadmin: number;
            admin: number;
            user: number;
        };
    };
    media: {
        total: number;
        total_size: number;
        by_type: {
            image: number;
            video: number;
            audio: number;
            document: number;
        };
        uploads_timeline: Array<{
            date: string;
            count: number;
        }>;
    };
    storage: {
        total_used: number;
        total_limit: number;
        usage_percentage: number;
        by_user_type: {
            free: number;
            basic: number;
            premium: number;
        };
    };
    recipients: {
        total: number;
        by_group: Record<string, number>;
    };
}

const Reports: React.FC = () => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState<string>('30days');
    const [reportType, setReportType] = useState<string>('all');
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
    const [downloadSuccess, setDownloadSuccess] = useState<boolean | null>(null);

    console?.log(stats)

    // Fetch report data
    const fetchReportData = async () => {
        setLoading(true);
        try {
            // For a complete implementation, you would need appropriate API endpoints for each report type
            let response: any;

            if (reportType === 'users' || reportType === 'all') {
                response = await AdminApi.getUserStats();
                if (response.data.status === 'success') {
                    setStats((prev: any) => ({
                        ...prev,
                        users: response.data.data
                    }));
                }
            }

            if (reportType === 'media' || reportType === 'all') {
                response = await AdminApi.getMediaStats();
                if (response.data.status === 'success') {
                    setStats((prev: any) => ({
                        ...prev,
                        media: response.data.data
                    }));
                }
            }

            if (reportType === 'storage' || reportType === 'all') {
                response = await AdminApi.getSystemStats();
                if (response.data.status === 'success') {
                    setStats((prev: any) => ({
                        ...prev,
                        storage: response.data.data.storage,
                        recipients: response.data.data.recipients
                    }));
                }
            }

            setError(null);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Error loading reports data');
            //   console?.error('Error fetching reports:', err);
        } finally {
            setLoading(false);
        }
    };

    // Initial data load
    useEffect(() => {
        fetchReportData();
    }, [timeRange, reportType]);

    // Format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes?.toFixed(2) + ' MB';
        else if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(2) + ' GB';
        else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' TB';
        else return (bytes / (1024 * 1024 * 1024))?.toFixed(2) + ' TB';
    };

    // Handle time range change
    const handleTimeRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTimeRange(event.target.value);
    };

    // Handle report type change
    const handleReportTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setReportType(event.target.value);
    };

    // Download report as CSV
    const handleDownloadReport = async () => {
        setDownloadLoading(true);
        setDownloadSuccess(null);

        try {
            const success = await downloadReport({
                reportType: reportType === 'all' ? 'complete' : reportType,
                timeRange: timeRange
            });

            setDownloadSuccess(success);

            // Reset the success message after 3 seconds
            if (success) {
                setTimeout(() => {
                    setDownloadSuccess(null);
                }, 3000);
            }
        } catch (error) {
            console.error('Error in download handler:', error);
            setDownloadSuccess(false);
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow px-6 py-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                    <h1 className="text-xl font-semibold text-gray-800 mb-4 md:mb-0">Reports & Analytics</h1>

                    <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 w-full md:w-auto">
                        <div className="relative">
                            <select
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="pl-10 pr-10 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                                <option value="7days">Last 7 Days</option>
                                <option value="30days">Last 30 Days</option>
                                <option value="90days">Last 90 Days</option>
                                <option value="year">Last Year</option>
                                <option value="all">All Time</option>
                            </select>
                            <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                            <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>

                        <div className="relative">
                            <select
                                value={reportType}
                                onChange={handleReportTypeChange}
                                className="pl-10 pr-10 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                                <option value="all">All Reports</option>
                                <option value="users">User Reports</option>
                                <option value="media">Media Reports</option>
                                <option value="storage">Storage Reports</option>
                            </select>
                            <Filter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                            <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>

                        <div className="flex space-x-2">
                            <button
                                onClick={fetchReportData}
                                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                disabled={loading}
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                <span>Refresh</span>
                            </button>

                            <button
                                onClick={handleDownloadReport}
                                className="flex items-center justify-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                                disabled={downloadLoading}
                            >
                                {downloadLoading ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        <span>Exporting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Download className="h-4 w-4 mr-2" />
                                        <span>Export</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {downloadSuccess !== null && (
                    <div className={`mt-2 p-2 rounded-md text-sm ${downloadSuccess ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                        {downloadSuccess ? (
                            <p className="flex items-center">
                                <Check className="h-4 w-4 mr-2" />
                                Report exported successfully
                            </p>
                        ) : (
                            <p className="flex items-center">
                                <AlertCircle className="h-4 w-4 mr-2" />
                                Failed to export report. Please try again.
                            </p>
                        )}
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {loading && !stats ? (
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-center items-center h-64">
                        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
                    </div>
                </div>
            ) : stats ? (
                <>
                    {/* User Statistics */}
                    {(reportType === 'users' || reportType === 'all') && (
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <Users className="h-5 w-5 text-blue-500 mr-2" />
                                    User Statistics
                                </h2>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <StatCard
                                        title="Total Users"
                                        value={stats.users.total}
                                        icon={<Users className="h-6 w-6 text-blue-500" />}
                                        change={stats.users.new_this_month}
                                        changeLabel="this month"
                                        positive={true}
                                    />

                                    <StatCard
                                        title="Active Users"
                                        value={stats.users.active}
                                        icon={<Users className="h-6 w-6 text-green-500" />}
                                        percentage={Math.round((stats.users.active / stats.users.total) * 100)}
                                        bgColor="bg-green-50"
                                    />

                                    <StatCard
                                        title="New Today"
                                        value={stats.users.new_today}
                                        icon={<Clock className="h-6 w-6 text-orange-500" />}
                                        change={stats.users.new_today - 2} // Replace with actual previous day value
                                        changeLabel="from yesterday"
                                        positive={stats.users.new_today > 2}
                                        bgColor="bg-orange-50"
                                    />

                                    <StatCard
                                        title="This Week"
                                        value={stats.users.new_this_week}
                                        icon={<Calendar className="h-6 w-6 text-purple-500" />}
                                        change={stats.users.new_this_week - 10} // Replace with actual previous week value
                                        changeLabel="from last week"
                                        positive={stats.users.new_this_week > 10}
                                        bgColor="bg-purple-50"
                                    />
                                </div>

                                <div className="mt-8">
                                    <h3 className="text-md font-medium text-gray-700 mb-4">User Growth Trend</h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200 h-80">
                                        <UserGrowthChart data={stats.users.by_month} />
                                    </div>
                                </div>

                                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-md font-medium text-gray-700 mb-4">User Distribution by Role</h3>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                                    <span className="text-sm text-gray-700">Regular Users</span>
                                                </div>
                                                <span className="text-sm font-medium">{stats.users.by_role.user}</span>
                                            </div>

                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                                                    <span className="text-sm text-gray-700">Admins</span>
                                                </div>
                                                <span className="text-sm font-medium">{stats.users.by_role.admin}</span>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                                                    <span className="text-sm text-gray-700">Superadmins</span>
                                                </div>
                                                <span className="text-sm font-medium">{stats.users.by_role.superadmin}</span>
                                            </div>

                                            <div className="mt-6 h-2 bg-gray-200 rounded-full">
                                                <div className="flex h-full rounded-full overflow-hidden">
                                                    <div
                                                        className="bg-green-500 h-full"
                                                        style={{ width: `${(stats.users.by_role.user / stats.users.total) * 100}%` }}
                                                    ></div>
                                                    <div
                                                        className="bg-blue-500 h-full"
                                                        style={{ width: `${(stats.users.by_role.admin / stats.users.total) * 100}%` }}
                                                    ></div>
                                                    <div
                                                        className="bg-purple-500 h-full"
                                                        style={{ width: `${(stats.users.by_role.superadmin / stats.users.total) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-md font-medium text-gray-700 mb-4">User Activity</h3>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                                    <span className="text-sm text-gray-700">Active Users</span>
                                                </div>
                                                <span className="text-sm font-medium">{stats.users.active}</span>
                                            </div>

                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                                                    <span className="text-sm text-gray-700">Inactive Users</span>
                                                </div>
                                                <span className="text-sm font-medium">{stats.users.inactive}</span>
                                            </div>

                                            <div className="mt-6 h-2 bg-gray-200 rounded-full">
                                                <div className="flex h-full rounded-full overflow-hidden">
                                                    <div
                                                        className="bg-green-500 h-full"
                                                        style={{ width: `${(stats.users.active / stats.users.total) * 100}%` }}
                                                    ></div>
                                                    <div
                                                        className="bg-red-500 h-full"
                                                        style={{ width: `${(stats.users.inactive / stats.users.total) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Media Statistics */}
                    {(reportType === 'media' || reportType === 'all') && (
                        <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <File className="h-5 w-5 text-blue-500 mr-2" />
                                    Media Statistics
                                </h2>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <StatCard
                                        title="Total Files"
                                        value={stats?.media?.total}
                                        icon={<File className="h-6 w-6 text-blue-500" />}
                                        bgColor="bg-blue-50"
                                    />

                                    <StatCard
                                        title="Total Storage"
                                        value={formatFileSize(stats?.media?.total_size)}
                                        isFormatted={true}
                                        icon={<HardDrive className="h-6 w-6 text-purple-500" />}
                                        bgColor="bg-purple-50"
                                    />

                                    <StatCard
                                        title="Images"
                                        value={stats?.media?.by_type?.image}
                                        icon={<File className="h-6 w-6 text-green-500" />}
                                        percentage={Math.round((stats?.media?.by_type?.image / stats?.media?.total) * 100)}
                                        bgColor="bg-green-50"
                                    />

                                    <StatCard
                                        title="Videos"
                                        value={stats?.media?.by_type?.video}
                                        icon={<File className="h-6 w-6 text-orange-500" />}
                                        percentage={Math.round((stats?.media?.by_type?.video / stats?.media?.total) * 100)}
                                        bgColor="bg-orange-50"
                                    />
                                </div>

                                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-md font-medium text-gray-700 mb-4">Media Type Distribution</h3>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200 h-80">
                                            <MediaTypeDistributionChart
                                                data={[
                                                    { type: 'Image', value: stats?.media?.by_type?.image },
                                                    { type: 'Video', value: stats?.media?.by_type?.video },
                                                    { type: 'Audio', value: stats?.media?.by_type?.audio },
                                                    { type: 'Document', value: stats?.media?.by_type?.document }
                                                ]}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-md font-medium text-gray-700 mb-4">Upload Activity</h3>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200 h-80">
                                            <ActivityTimelineChart data={stats?.media?.uploads_timeline} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Storage Statistics */}
                    {(reportType === 'storage' || reportType === 'all') && (
                        <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <HardDrive className="h-5 w-5 text-blue-500 mr-2" />
                                    Storage Statistics
                                </h2>
                            </div>

                            <div className="p-6">
                                <div className="bg-blue-50 rounded-lg p-6 mb-8">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Total Storage Used</p>
                                            <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats?.storage?.total_used)}</p>
                                        </div>

                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Storage Limit</p>
                                            <p className="text-2xl font-bold text-gray-900">{formatFileSize(stats?.storage?.total_limit)}</p>
                                        </div>

                                        <div>
                                            <p className="text-sm text-gray-500 mb-1">Usage</p>
                                            <p className="text-2xl font-bold text-gray-900">{stats?.storage?.usage_percentage?.toFixed(2)}%</p>
                                        </div>
                                    </div>

                                    <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${stats?.storage?.usage_percentage > 90 ? 'bg-red-500' :
                                                    stats?.storage?.usage_percentage > 70 ? 'bg-orange-500' : 'bg-green-500'
                                                }`}
                                            style={{ width: `${Math.min(stats?.storage?.usage_percentage, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <h3 className="text-md font-medium text-gray-700 mb-4">Storage Distribution by Plan</h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200 h-80">
                                        <StorageUsageChart
                                            data={[
                                                { plan: 'Free', value: stats?.storage?.by_user_type?.free },
                                                { plan: 'Gold', value: stats?.storage?.by_user_type?.basic },
                                                { plan: 'Premium', value: stats?.storage?.by_user_type?.premium }
                                            ]}
                                        />
                                    </div>
                                </div>

                                {/* <div className="mt-8">
                                    <h3 className="text-md font-medium text-gray-700 mb-4">Recipient Groups</h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {stats?.recipients?.by_group ?
                                                Object.entries(stats.recipients.by_group).map(([group, count]) => (
                                                    <div key={group} className="bg-gray-50 p-4 rounded-lg">
                                                        <p className="text-sm text-gray-500 mb-1">Group: {group}</p>
                                                        <p className="text-xl font-bold text-gray-900">{count} recipients</p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {((count / stats.recipients.total) * 100).toFixed(1)}% of total
                                                        </p>
                                                    </div>
                                                ))
                                                : <p className="text-sm text-gray-500">No recipient groups available</p>}
                                        </div>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default Reports;